import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import {
    getInvoiceRevenueAllocation,
    reallocateInvoiceRevenue,
} from '@/modules/Report/components/Revenue/services'
import { formatNumeral } from '@/utils/function'
import { useTranslation } from 'react-i18next'
import { Dialog, Button, Chip, TextField } from 'vnetwork-ui'
import CellPrice from '@/components/GokuDataGrid/CellPrice'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { ModalProps } from '@/types/shared'
import { InvoiceRevenueAllocation } from '@/modules/Report/components/Revenue/types'
import { useMemo, useState } from 'react'
import {
    ChevronDown,
    ChevronRight,
    Edit2,
    Save,
    X,
    AlertTriangle,
    CheckCircle2,
    Trash2,
    Plus,
    CalendarDays,
    List,
} from 'lucide-react'
import MonthlyGroupedView from './MonthlyGroupedView'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useConfirmation } from '@/contexts/confirmation'

export interface RevenueAllocationPreviewProps extends ModalProps {
    invoiceID: number
}

const RevenueAllocationPreview: React.FC<RevenueAllocationPreviewProps> = ({
    invoiceID,
    onClose,
    open,
}) => {
    const { t } = useTranslation()
    const { setConfirmation } = useConfirmation()
    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())
    const [viewMode, setViewMode] = useState<'items' | 'monthly'>('items')
    const [editMode, setEditMode] = useState(false)
    const [editedAllocations, setEditedAllocations] = useState<
        InvoiceRevenueAllocation[]
    >([])

    const { data } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.INVOICE_REVENUE_ALLOCATION, invoiceID],
        queryFn: () => getInvoiceRevenueAllocation(invoiceID),
        enabled: !!invoiceID,
    })

    const allocations = data || []

    // Calculate summary statistics from allocations
    const summary = useMemo(() => {
        if (!allocations.length) return null

        const totalAmount = allocations.reduce(
            (sum, item) => sum + (item.total_amount || 0),
            0
        )
        const paidRecords = allocations.filter((item) => item.is_paid)
        const unpaidRecords = allocations.filter((item) => !item.is_paid)
        const totalPaidAmount = paidRecords.reduce(
            (sum, item) => sum + (item.total_amount || 0),
            0
        )
        const totalUnpaidAmount = unpaidRecords.reduce(
            (sum, item) => sum + (item.total_amount || 0),
            0
        )

        return {
            total_records: allocations.length,
            total_allocated_amount: totalAmount,
            total_paid_records: paidRecords.length,
            total_unpaid_records: unpaidRecords.length,
            total_paid_amount: totalPaidAmount,
            total_unpaid_amount: totalUnpaidAmount,
        }
    }, [allocations])

    const toggleRow = (id: number) => {
        setExpandedRows((prev) => {
            const newSet = new Set(prev)
            if (newSet.has(id)) {
                newSet.delete(id)
            } else {
                newSet.add(id)
            }
            return newSet
        })
    }

    // Mutation for reallocating revenue
    const { mutateAsync: reallocate, isPending: isReallocating } =
        useMutationWithHandler(
            { mutationFn: reallocateInvoiceRevenue },
            {
                reactQueryKeys: [QUERY_KEYS.INVOICE_REVENUE_ALLOCATION],
                successMessage:
                    t('revenue-reallocated-successfully') ||
                    'Revenue reallocated successfully',
                errorMessage:
                    t('failed-to-reallocate-revenue') ||
                    'Failed to reallocate revenue',
                onSuccess: () => {
                    setEditMode(false)
                    setEditedAllocations([])
                },
            }
        )
    // Enter edit mode
    const handleEnterEditMode = () => {
        setEditedAllocations(JSON.parse(JSON.stringify(allocations))) // Deep clone
        setEditMode(true)
        // Expand all rows in edit mode
        const allIds = allocations
            .filter((a) => a.revenue_allocations?.length)
            .map((a) => a.id)
        setExpandedRows(new Set(allIds))
    }

    // Exit edit mode without saving
    const handleCancelEdit = () => {
        setEditMode(false)
        setEditedAllocations([])
    }

    // Update monthly allocation amount
    const handleUpdateMonthlyAmount = (
        invoiceItemId: number,
        monthlyAllocId: number,
        newAmount: string
    ) => {
        setEditedAllocations((prev) =>
            prev.map((item) => {
                if (item.invoice_item_id === invoiceItemId) {
                    return {
                        ...item,
                        revenue_allocations: item.revenue_allocations?.map(
                            (ma) =>
                                ma.id === monthlyAllocId
                                    ? {
                                          ...ma,
                                          allocated_amount:
                                              parseFloat(newAmount) || 0,
                                      }
                                    : ma
                        ),
                    }
                }
                return item
            })
        )
    }

    // Delete monthly allocation row
    const handleDeleteMonthlyAllocation = (
        invoiceItemId: number,
        monthlyAllocId: number
    ) => {
        setEditedAllocations((prev) =>
            prev.map((item) => {
                if (item.invoice_item_id === invoiceItemId) {
                    return {
                        ...item,
                        revenue_allocations: item.revenue_allocations?.filter(
                            (ma) => ma.id !== monthlyAllocId
                        ),
                    }
                }
                return item
            })
        )
    }

    // Add new monthly allocation row
    const handleAddMonthlyAllocation = (invoiceItemId: number) => {
        setEditedAllocations((prev) =>
            prev.map((item) => {
                if (item.invoice_item_id === invoiceItemId) {
                    const newId =
                        Math.max(
                            0,
                            ...(item.revenue_allocations?.map((ma) => ma.id) ||
                                [])
                        ) + 1
                    const newAllocation = {
                        id: newId,
                        revenue_month: new Date().toISOString().slice(0, 7), // YYYY-MM format
                        allocated_amount: 0,
                        revenue_type: item.invoice_type,
                        is_recognized: false,
                    }

                    return {
                        ...item,
                        revenue_allocations: [
                            ...(item.revenue_allocations || []),
                            newAllocation,
                        ],
                    }
                }
                return item
            })
        )
    }

    // Update revenue month
    const handleUpdateRevenueMonth = (
        invoiceItemId: number,
        monthlyAllocId: number,
        newMonth: string
    ) => {
        setEditedAllocations((prev) =>
            prev.map((item) => {
                if (item.invoice_item_id === invoiceItemId) {
                    return {
                        ...item,
                        revenue_allocations: item.revenue_allocations?.map(
                            (ma) =>
                                ma.id === monthlyAllocId
                                    ? { ...ma, revenue_month: newMonth }
                                    : ma
                        ),
                    }
                }
                return item
            })
        )
    }

    // Validate that sum of monthly allocations equals total amount
    const validateAllocations = (): { isValid: boolean; errors: string[] } => {
        const errors: string[] = []

        editedAllocations.forEach((item) => {
            if (
                item.revenue_allocations &&
                item.revenue_allocations.length > 0
            ) {
                const sumAllocated = item.revenue_allocations.reduce(
                    (sum, ma) => sum + (ma.allocated_amount || 0),
                    0
                )
                const totalAmount = item.total_amount || 0

                // Allow small floating point differences (0.01)
                if (Math.abs(sumAllocated - totalAmount) > 0.01) {
                    errors.push(
                        `${item.product_group_name}: ${t('sum-allocated') || 'Sum'} (${formatNumeral(sumAllocated, 0)}) ≠ ${t('total-amount') || 'Total'} (${formatNumeral(totalAmount, 0)})`
                    )
                }
            }
        })

        return {
            isValid: errors.length === 0,
            errors,
        }
    }

    // Submit reallocated data
    const handleSubmitReallocation = () => {
        // Validate before submitting
        const validation = validateAllocations()

        if (!validation.isValid) {
            setConfirmation({
                open: true,
                title: t('validation-error') || 'Validation Error',
                description:
                    (t('allocation-sum-mismatch') ||
                        'The sum of monthly allocations must equal the total amount for each item:') +
                    '\n\n' +
                    validation.errors.join('\n'),
                onConfirm: () => {
                    // Just close the dialog
                },
            })
            return
        }

        setConfirmation({
            open: true,
            title: t('confirm-reallocation') || 'Confirm Reallocation',
            description:
                t('reallocation-warning') ||
                'WARNING: This will DELETE all existing revenue allocations and create new ones based on your edits. This action cannot be undone. Are you sure you want to proceed?',
            onConfirm: async () => {
                // Prepare payload
                const payload = {
                    invoice_id: invoiceID,
                    invoice_items: editedAllocations
                        .filter(
                            (item) =>
                                item.revenue_allocations &&
                                item.revenue_allocations.length > 0
                        )
                        .map((item) => ({
                            invoice_item_id: item.invoice_item_id,
                            revenue_allocations: item.revenue_allocations!.map(
                                (ma) => ({
                                    revenue_month: ma.revenue_month,
                                    allocated_amount: ma.allocated_amount,
                                })
                            ),
                        })),
                }

                await reallocate(payload)
            },
        })
    }

    return (
        <Dialog
            onClose={onClose}
            open={open}
            className="relative"
            title={
                t('revenue_allocation_preview') || 'Revenue Allocation Preview'
            }
            maxWidth="full"
        >
            {/* Fixed Header */}
            <div className="text-sm text-gray-600 mb-4">
                {t('invoice')} #{invoiceID}
            </div>

            {/* Summary Cards - Fixed */}
            {summary && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="text-sm text-gray-600 mb-1">
                            {t('total_allocated_amount') || 'Total Amount'}
                        </div>
                        <div className="text-2xl font-bold text-blue-700">
                            <CellPrice
                                price={summary.total_allocated_amount || 0}
                            />
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                            {formatNumeral(summary.total_records || 0, 0)}{' '}
                            {t('items') || 'items'}
                        </div>
                    </div>

                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="text-sm text-gray-600 mb-1">
                            {t('paid_amount') || 'Paid'}
                        </div>
                        <div className="text-2xl font-bold text-green-700">
                            <CellPrice price={summary.total_paid_amount || 0} />
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                            {formatNumeral(summary.total_paid_records || 0, 0)}{' '}
                            {t('items') || 'items'}
                        </div>
                    </div>

                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <div className="text-sm text-gray-600 mb-1">
                            {t('unpaid_amount') || 'Unpaid'}
                        </div>
                        <div className="text-2xl font-bold text-amber-700">
                            <CellPrice
                                price={summary.total_unpaid_amount || 0}
                            />
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                            {formatNumeral(
                                summary.total_unpaid_records || 0,
                                0
                            )}{' '}
                            {t('items') || 'items'}
                        </div>
                    </div>
                </div>
            )}

            {/* View Mode Toggle */}
            {allocations.length > 0 && !editMode && (
                <div className="flex items-center gap-1 mb-4">
                    <div className="inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50">
                        <button
                            onClick={() => setViewMode('items')}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                                viewMode === 'items'
                                    ? 'bg-white text-gray-900 shadow-sm'
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <List className="w-3.5 h-3.5" />
                            {t('by_items') || 'By Items'}
                        </button>
                        <button
                            onClick={() => setViewMode('monthly')}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                                viewMode === 'monthly'
                                    ? 'bg-white text-gray-900 shadow-sm'
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <CalendarDays className="w-3.5 h-3.5" />
                            {t('by_month') || 'By Month'}
                        </button>
                    </div>
                </div>
            )}

            {/* Scrollable Table Container */}
            {allocations.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <p className="text-gray-500">
                        {t('no_revenue_allocation') ||
                            'No revenue allocation found for this invoice'}
                    </p>
                </div>
            ) : viewMode === 'monthly' && !editMode ? (
                <MonthlyGroupedView allocations={allocations} t={t} />
            ) : (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="max-h-[60vh] overflow-y-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                                <tr>
                                    <th className="px-2 py-3 w-10"></th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {t('billing_period') ||
                                            'Billing Period'}
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {t('product_group') || 'Product Group'}
                                    </th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {t('allocated_amount') || 'Amount'}
                                    </th>
                                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {t('invoice_type') || 'Type'}
                                    </th>
                                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {t('payment_status') || 'Payment'}
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {(editMode
                                    ? editedAllocations
                                    : allocations
                                ).map(
                                    (
                                        allocation: InvoiceRevenueAllocation,
                                        index: number
                                    ) => {
                                        const isExpanded = expandedRows.has(
                                            allocation.id
                                        )
                                        const hasMonthlyAllocations =
                                            allocation.revenue_allocations &&
                                            allocation.revenue_allocations
                                                .length > 0

                                        return (
                                            <>
                                                <tr
                                                    key={allocation.id || index}
                                                    className="hover:bg-gray-50"
                                                >
                                                    <td className="px-2 py-3 w-10">
                                                        {hasMonthlyAllocations && (
                                                            <button
                                                                onClick={() =>
                                                                    toggleRow(
                                                                        allocation.id
                                                                    )
                                                                }
                                                                className="text-gray-500 hover:text-gray-700"
                                                            >
                                                                {isExpanded ? (
                                                                    <ChevronDown className="w-4 h-4" />
                                                                ) : (
                                                                    <ChevronRight className="w-4 h-4" />
                                                                )}
                                                            </button>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                                        {
                                                            allocation.billing_period_start.split(
                                                                'T'
                                                            )[0]
                                                        }{' '}
                                                        -{' '}
                                                        {
                                                            allocation.billing_period_end.split(
                                                                'T'
                                                            )[0]
                                                        }
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-700">
                                                        {
                                                            allocation.product_group_name
                                                        }
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-semibold text-gray-900">
                                                        <CellPrice
                                                            price={
                                                                allocation.total_amount ||
                                                                0
                                                            }
                                                        />
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-center">
                                                        <Chip
                                                            label={
                                                                t(
                                                                    `invoice_type.${allocation.invoice_type}`
                                                                ) ||
                                                                allocation.invoice_type
                                                            }
                                                            color={
                                                                allocation.invoice_type ===
                                                                'renewal'
                                                                    ? 'primary'
                                                                    : allocation.invoice_type ===
                                                                        'new_sale'
                                                                      ? 'success'
                                                                      : 'warning'
                                                            }
                                                            size="small"
                                                        />
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-center">
                                                        <Chip
                                                            label={
                                                                allocation.is_paid
                                                                    ? t(
                                                                          'paid'
                                                                      ) ||
                                                                      'Paid'
                                                                    : t(
                                                                          'unpaid'
                                                                      ) ||
                                                                      'Unpaid'
                                                            }
                                                            color={
                                                                allocation.is_paid
                                                                    ? 'success'
                                                                    : 'default'
                                                            }
                                                            size="small"
                                                        />
                                                    </td>
                                                </tr>

                                                {/* Monthly Allocations Expanded Row */}
                                                {isExpanded &&
                                                    hasMonthlyAllocations && (
                                                        <tr
                                                            key={`${allocation.id}-expanded`}
                                                            className="bg-gray-50"
                                                        >
                                                            <td
                                                                colSpan={6}
                                                                className="px-4 py-3"
                                                            >
                                                                <div className="ml-8">
                                                                    <div className="text-xs font-semibold text-gray-700 mb-2">
                                                                        {t(
                                                                            'monthly_allocation_breakdown'
                                                                        ) ||
                                                                            'Monthly Allocation Breakdown'}
                                                                    </div>
                                                                    <div className="border border-gray-300 rounded bg-white">
                                                                        <table className="w-full">
                                                                            <thead className="bg-gray-100 border-b border-gray-300">
                                                                                <tr>
                                                                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">
                                                                                        {t(
                                                                                            'revenue_month'
                                                                                        ) ||
                                                                                            'Month'}
                                                                                    </th>
                                                                                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-600">
                                                                                        {t(
                                                                                            'allocated_amount'
                                                                                        ) ||
                                                                                            'Amount'}
                                                                                    </th>
                                                                                    <th className="px-3 py-2 text-center text-xs font-medium text-gray-600">
                                                                                        {t(
                                                                                            'revenue_type'
                                                                                        ) ||
                                                                                            'Type'}
                                                                                    </th>
                                                                                    <th className="px-3 py-2 text-center text-xs font-medium text-gray-600">
                                                                                        {t(
                                                                                            'status'
                                                                                        ) ||
                                                                                            'Status'}
                                                                                    </th>
                                                                                    {editMode && (
                                                                                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 w-20">
                                                                                            {t(
                                                                                                'actions'
                                                                                            ) ||
                                                                                                'Actions'}
                                                                                        </th>
                                                                                    )}
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody className="divide-y divide-gray-200">
                                                                                {allocation.revenue_allocations!.map(
                                                                                    (
                                                                                        monthlyAlloc
                                                                                    ) => (
                                                                                        <tr
                                                                                            key={
                                                                                                monthlyAlloc.id
                                                                                            }
                                                                                            className="hover:bg-gray-50"
                                                                                        >
                                                                                            <td className="px-3 py-2 text-sm text-gray-900">
                                                                                                {editMode ? (
                                                                                                    <TextField
                                                                                                        type="month"
                                                                                                        value={
                                                                                                            monthlyAlloc.revenue_month
                                                                                                        }
                                                                                                        onChange={(
                                                                                                            e
                                                                                                        ) =>
                                                                                                            handleUpdateRevenueMonth(
                                                                                                                allocation.invoice_item_id,
                                                                                                                monthlyAlloc.id,
                                                                                                                e
                                                                                                                    .target
                                                                                                                    .value
                                                                                                            )
                                                                                                        }
                                                                                                        size="small"
                                                                                                        className="w-40"
                                                                                                    />
                                                                                                ) : (
                                                                                                    monthlyAlloc.revenue_month
                                                                                                )}
                                                                                            </td>
                                                                                            <td className="px-3 py-2 text-sm text-right font-medium text-gray-900">
                                                                                                {editMode ? (
                                                                                                    <div className="flex justify-end">
                                                                                                        <TextField
                                                                                                            type="number"
                                                                                                            value={
                                                                                                                monthlyAlloc.allocated_amount ||
                                                                                                                0
                                                                                                            }
                                                                                                            onChange={(
                                                                                                                e
                                                                                                            ) =>
                                                                                                                handleUpdateMonthlyAmount(
                                                                                                                    allocation.invoice_item_id,
                                                                                                                    monthlyAlloc.id,
                                                                                                                    e
                                                                                                                        .target
                                                                                                                        .value
                                                                                                                )
                                                                                                            }
                                                                                                            size="small"
                                                                                                            className="w-32"
                                                                                                        />
                                                                                                    </div>
                                                                                                ) : (
                                                                                                    <CellPrice
                                                                                                        price={
                                                                                                            monthlyAlloc.allocated_amount ||
                                                                                                            0
                                                                                                        }
                                                                                                    />
                                                                                                )}
                                                                                            </td>
                                                                                            <td className="px-3 py-2 text-center">
                                                                                                <Chip
                                                                                                    label={
                                                                                                        t(
                                                                                                            `invoice_type.${monthlyAlloc.revenue_type}`
                                                                                                        ) ||
                                                                                                        monthlyAlloc.revenue_type
                                                                                                    }
                                                                                                    color={
                                                                                                        monthlyAlloc.revenue_type ===
                                                                                                        'renewal'
                                                                                                            ? 'primary'
                                                                                                            : monthlyAlloc.revenue_type ===
                                                                                                                'new_sale'
                                                                                                              ? 'success'
                                                                                                              : 'warning'
                                                                                                    }
                                                                                                    size="small"
                                                                                                />
                                                                                            </td>
                                                                                            <td className="px-3 py-2 text-center">
                                                                                                <Chip
                                                                                                    label={
                                                                                                        monthlyAlloc.is_recognized
                                                                                                            ? t(
                                                                                                                  'recognized'
                                                                                                              ) ||
                                                                                                              'Recognized'
                                                                                                            : t(
                                                                                                                  'unrecognized'
                                                                                                              ) ||
                                                                                                              'Unrecognized'
                                                                                                    }
                                                                                                    color={
                                                                                                        monthlyAlloc.is_recognized
                                                                                                            ? 'success'
                                                                                                            : 'default'
                                                                                                    }
                                                                                                    size="small"
                                                                                                />
                                                                                            </td>
                                                                                            {editMode && (
                                                                                                <td className="px-3 py-2 text-center">
                                                                                                    <button
                                                                                                        onClick={() =>
                                                                                                            handleDeleteMonthlyAllocation(
                                                                                                                allocation.invoice_item_id,
                                                                                                                monthlyAlloc.id
                                                                                                            )
                                                                                                        }
                                                                                                        className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50"
                                                                                                        title={
                                                                                                            t(
                                                                                                                'delete'
                                                                                                            ) ||
                                                                                                            'Delete'
                                                                                                        }
                                                                                                    >
                                                                                                        <Trash2 className="w-4 h-4" />
                                                                                                    </button>
                                                                                                </td>
                                                                                            )}
                                                                                        </tr>
                                                                                    )
                                                                                )}
                                                                            </tbody>
                                                                            {editMode &&
                                                                                (() => {
                                                                                    const sumAllocated =
                                                                                        allocation.revenue_allocations!.reduce(
                                                                                            (
                                                                                                sum,
                                                                                                ma
                                                                                            ) =>
                                                                                                sum +
                                                                                                (ma.allocated_amount ||
                                                                                                    0),
                                                                                            0
                                                                                        )
                                                                                    const totalAmount =
                                                                                        allocation.total_amount ||
                                                                                        0
                                                                                    const difference =
                                                                                        Math.abs(
                                                                                            sumAllocated -
                                                                                                totalAmount
                                                                                        )
                                                                                    const isValid =
                                                                                        difference <=
                                                                                        0.01

                                                                                    return (
                                                                                        <tfoot className="bg-gray-100 border-t-2 border-gray-400">
                                                                                            <tr>
                                                                                                <td className="px-3 py-2 text-xs font-semibold text-gray-700">
                                                                                                    {t(
                                                                                                        'total'
                                                                                                    ) ||
                                                                                                        'Total'}
                                                                                                </td>
                                                                                                <td className="px-3 py-2 text-sm text-right font-bold">
                                                                                                    <div className="flex items-center justify-end gap-2">
                                                                                                        <CellPrice
                                                                                                            price={
                                                                                                                sumAllocated
                                                                                                            }
                                                                                                        />
                                                                                                        {isValid ? (
                                                                                                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                                                                                                        ) : (
                                                                                                            <AlertTriangle className="w-4 h-4 text-red-600" />
                                                                                                        )}
                                                                                                    </div>
                                                                                                </td>
                                                                                                <td
                                                                                                    colSpan={
                                                                                                        3
                                                                                                    }
                                                                                                    className="px-3 py-2 text-xs"
                                                                                                >
                                                                                                    {!isValid && (
                                                                                                        <div className="text-red-600 font-semibold">
                                                                                                            {t(
                                                                                                                'should-equal'
                                                                                                            ) ||
                                                                                                                'Should equal'}

                                                                                                            :{' '}
                                                                                                            <CellPrice
                                                                                                                price={
                                                                                                                    totalAmount
                                                                                                                }
                                                                                                            />
                                                                                                            <span className="ml-2">
                                                                                                                (
                                                                                                                {t(
                                                                                                                    'diff'
                                                                                                                ) ||
                                                                                                                    'Diff'}

                                                                                                                :{' '}
                                                                                                                <CellPrice
                                                                                                                    price={
                                                                                                                        difference
                                                                                                                    }
                                                                                                                />

                                                                                                                )
                                                                                                            </span>
                                                                                                        </div>
                                                                                                    )}
                                                                                                    {isValid && (
                                                                                                        <div className="text-green-600 font-semibold">
                                                                                                            {t(
                                                                                                                'sum-matches-total'
                                                                                                            ) ||
                                                                                                                'Sum matches total'}
                                                                                                        </div>
                                                                                                    )}
                                                                                                </td>
                                                                                            </tr>
                                                                                            <tr className="border-t border-gray-300">
                                                                                                <td
                                                                                                    colSpan={
                                                                                                        5
                                                                                                    }
                                                                                                    className="px-3 py-2"
                                                                                                >
                                                                                                    <button
                                                                                                        onClick={() =>
                                                                                                            handleAddMonthlyAllocation(
                                                                                                                allocation.invoice_item_id
                                                                                                            )
                                                                                                        }
                                                                                                        className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded border-2 border-dashed border-blue-300"
                                                                                                    >
                                                                                                        <Plus className="w-4 h-4" />
                                                                                                        {t(
                                                                                                            'add-month'
                                                                                                        ) ||
                                                                                                            'Add Month'}
                                                                                                    </button>
                                                                                                </td>
                                                                                            </tr>
                                                                                        </tfoot>
                                                                                    )
                                                                                })()}
                                                                        </table>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                            </>
                                        )
                                    }
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Fixed Action Buttons */}
            <div className="flex justify-between items-center gap-2 mt-6 pt-4 border-t">
                <div>
                    {editMode && (
                        <div className="text-sm text-amber-600 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4" />
                            <span>
                                {t('edit-mode-active') || 'Edit Mode Active'}
                            </span>
                        </div>
                    )}
                </div>
                <div className="flex gap-2">
                    {editMode ? (
                        <>
                            <Button
                                variant="outlined"
                                color="default"
                                onClick={handleCancelEdit}
                                startIcon={<X className="w-4 h-4" />}
                                disabled={isReallocating}
                            >
                                {t('cancel') || 'Cancel'}
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSubmitReallocation}
                                startIcon={<Save className="w-4 h-4" />}
                                loading={isReallocating}
                                disabled={isReallocating}
                            >
                                {t('save-reallocation') || 'Save & Reallocate'}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                variant="outlined"
                                color="default"
                                onClick={onClose}
                            >
                                {t('close') || 'Close'}
                            </Button>
                            {allocations.some(
                                (a) =>
                                    a.revenue_allocations &&
                                    a.revenue_allocations.length > 0
                            ) && (
                                <Button
                                    variant="contained"
                                    color="info"
                                    onClick={handleEnterEditMode}
                                    startIcon={<Edit2 className="w-4 h-4" />}
                                >
                                    {t('reallocate') || 'Reallocate'}
                                </Button>
                            )}
                        </>
                    )}
                </div>
            </div>
        </Dialog>
    )
}

export default RevenueAllocationPreview
